import {
  I18NState,
  LanguageChangeListenerCallback,
  LanguageIdentifier,
  LanguageInterface,
  resolveTranslation,
} from "..";

/**
 * Gets the current language.
 * @returns The current language.
 */
export function getCurrentLanguageSync() {
  return I18NState.getInstance().language;
}

/**
 * Registers a new language.
 *
 * @param key - The language identifier.
 * @param label - The display name of the language.
 * @param parent - The parent language identifier, if any.
 * @param isDefault - Whether this language is the default language.
 */
export function registerLanguage(
  key: LanguageIdentifier,
  label: string,
  parent: LanguageIdentifier = null,
  isDefault: boolean = false
) {
  const STATE = I18NState.getInstance();
  if (key === parent) {
    throw new Error(
      `@opendash/i18n: registerLanguage Error key === parent is forbidden`
    );
  }
  const lang: LanguageInterface = { key, label, parent, default: isDefault };
  STATE.addLanguage(lang);

  STATE.addTranslation(key, {});
}

/**
 * Registers a callback function to be called when the language changes.
 *
 * @param callback - The function to be called when the language changes.
 * @returns A function to unregister the callback.
 */
export function onLanguageChange(callback: LanguageChangeListenerCallback) {
  const STATE = I18NState.getInstance();
  STATE.languageChangeListener.add(callback);

  return () => {
    STATE.languageChangeListener.delete(callback);
  };
}

/**
 * Changes the current language.
 *
 * @param language - The language identifier to change to.
 * @returns A promise that resolves when the language change is complete.
 */
export async function changeLanguage(language: LanguageIdentifier) {
  const STATE = I18NState.getInstance();
  if (!STATE.languages[language]) {
    return void console.warn(
      `@opendash/i18n: Can not change language to '${language}'. You first need to register the language.`
    );
  }

  STATE.language = language;

  try {
    await Promise.all(
      Array.from(STATE.namespacesInUse.values()).map((ns) =>
        resolveTranslation(ns)
      )
    );
  } catch (error) {
    console.error(`@opendash/i18n: Error while resolving translations:`);
    console.error(error);
  }

  for (const callback of Array.from(STATE.languageChangeListener)) {
    Promise.resolve()
      .then(() => {
        if (typeof callback === "function") {
          callback(STATE.language);
        } else {
          console.error(
            `@opendash/i18n: callback is not a function:`,
            callback
          );
        }
      })
      .catch((error) => {
        console.error(`@opendash/i18n: Error in language change listener:`);
        console.error(error);
      });
  }
}
